import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", starting_balance: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        starting_balance: parseFloat(form.starting_balance || "0"),
      });
      // If the user arrived from the public SMS signup page, they consented to
      // SMS there already. Persist the phone number to their account so they
      // don't have to re-enter it on Profile → SMS.
      try {
        const pendingPhone = window.sessionStorage.getItem(
          "syncbook_pending_sms_phone"
        );
        if (pendingPhone) {
          await api.put("/auth/phone", { phone_number: pendingPhone });
          window.sessionStorage.removeItem("syncbook_pending_sms_phone");
        }
      } catch (err) {
        console.debug("pending SMS phone attach failed:", err?.message);
      }
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#fdecc8] opacity-70 blur-2xl" />
      <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-[#d9d4f1] opacity-70 blur-2xl" />
      <div className="relative w-full max-w-md">
        <div className="mb-5 text-center">
          <img
            src={LOGO_URL}
            alt="SyncBook"
            className="mx-auto w-full max-w-[280px] rounded-2xl border-2 border-[#1a1a1a] shadow-[4px_4px_0_0_#1a1a1a] mb-4"
          />
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Get set up.</h1>
          <p className="text-sm text-[#4a4a4a] mt-2 font-medium">Track what you spend. Own your money.</p>
        </div>
        <form onSubmit={submit} className="nb-card-lg p-6 space-y-4" data-testid="signup-form">
          <div>
            <label className="label">Your name</label>
            <input
              data-testid="signup-name"
              className="nb-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Alex Rivera"
            />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              data-testid="signup-email"
              type="email"
              required
              className="nb-input"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input
              data-testid="signup-password"
              type="password"
              required
              className="nb-input"
              value={form.password}
              onChange={set("password")}
              placeholder="At least 6 characters"
              minLength={6}
            />
          </div>
          <div>
            <label className="label">Starting balance</label>
            <input
              data-testid="signup-starting-balance"
              type="number"
              step="0.01"
              className="nb-input"
              value={form.starting_balance}
              onChange={set("starting_balance")}
              placeholder="0.00"
            />
          </div>
          <button
            data-testid="signup-submit-btn"
            disabled={busy}
            className="nb-btn nb-btn-dark w-full !py-3"
            type="submit"
          >
            {busy ? "Creating…" : "Create account"}
          </button>
          <p className="text-center text-sm text-[#4a4a4a] font-medium">
            Already have one?{" "}
            <Link data-testid="go-login-link" to="/login" className="font-bold underline">
              Sign in
            </Link>
          </p>
          <p className="text-center text-[11px] text-[#4a4a4a] font-medium pt-1">
            By creating an account you agree to our{" "}
            <Link
              to="/terms"
              data-testid="signup-terms-link"
              className="underline font-bold"
            >
              Terms
            </Link>{" "}
            &{" "}
            <Link
              to="/privacy"
              data-testid="signup-privacy-link"
              className="underline font-bold"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </form>
      </div>
    </div>
  );
}
